import { ADMIN_BASE, AthenaError, call, query } from './client.ts'
import {
  decodeDetectionPage,
  decodeLogDetail,
  decodeProcessTree,
  decodeTimelinePage,
} from '../sse/parse.ts'
import type {
  DetectionPage,
  DetectionSeverity,
  LogDetail,
  LogRow,
  ProcessTimelinePage,
  ProcessTree,
} from '../sse/types.ts'

/**
 * A superfície REST do console (`/api/admin`).
 *
 * Vizinha de `resources.ts`, mas em arquivo separado porque é outra convenção de
 * corpo: aqui os corpos vêm em snake_case, e o prefixo é `ADMIN_BASE`. As chaves
 * de query string, no entanto, seguem camelCase nas duas superfícies (§0.1) — por
 * isso `query({ tenantId })`, nunca `tenant_id`.
 *
 * Como o resto do transporte, só o transporte mora aqui: nada de estado, cache ou
 * tradução de erro. Quem chama trata o `AthenaError` pelo `code`.
 */

// ── diretório de empresas e agentes (§2.2) ───────────────────────────────────
//
// Nenhum dos dois toca o banco: são servidos de fotografias em memória,
// atualizadas a cada 10 s (empresas) e 30 s (agentes). `stale: true` = a última
// atualização falhou e você vê o último valor conhecido — marque, não esconda.

export interface TenantOption {
  tenant_id: string
  name: string
  /** Endpoints provisionados da empresa. */
  endpoint_count: number
}

export interface TenantDirectory {
  items: TenantOption[]
  captured_at: string
  stale: boolean
}

/**
 * Estado do agente segundo a varredura de silêncio. `unknown` é o período antes
 * da primeira varredura. Vocabulário aberto: valor novo vai como veio.
 */
export type AgentState =
  | 'connected'
  | 'anomalous_silence'
  | 'expected_offline'
  | 'never_connected'
  | 'unknown'
  | (string & {})

export interface AgentDirectoryItem {
  endpoint_id: string
  tenant_id: string
  tenant_name: string | null
  hostname: string | null
  agent_version: string | null
  last_seen_at: string | null
  state: AgentState
  /** Presente nos estados de silêncio. */
  silent_for_minutes: number | null
}

export interface AgentDirectoryPage {
  items: AgentDirectoryItem[]
  total_count: number
  page: number
  page_size: number
  captured_at: string
  stale: boolean
}

export interface AgentDirectoryQuery {
  page?: number
  pageSize?: number
  /** Só `/api/admin`: a rota de cliente nunca recebe empresa. */
  tenantId?: string
  /** Casa hostname OU nome da empresa, sem diferenciar maiúsculas. */
  search?: string
}

// ── estatísticas (§4.7, §4.8, §5.8) ──────────────────────────────────────────

export interface SeverityCounts {
  low: number
  medium: number
  high: number
  critical: number
  unknown: number
  total: number
}

export interface DetectionStatsTenant {
  tenant_id: string
  tenant_name: string | null
  counts: SeverityCounts
}

export interface DetectionStats {
  from: string
  to: string
  totals: SeverityCounts
  tenants: DetectionStatsTenant[]
}

export interface DetectionStatsQuery {
  /** ISO-8601. Padrão do servidor: últimas 24 h; teto de 31 dias. */
  from?: string
  to?: string
  /** Só `/api/admin`. */
  tenantId?: string
}

export interface VolumeTenant {
  tenant_id: string
  tenant_name: string | null
  endpoints: number
  events_total: number
}

export interface VolumeEndpoint {
  endpoint_id: string
  hostname: string | null
  events_total: number
  /** `YYYY-MM-DD`. */
  last_active_date: string | null
}

export interface VolumeStats {
  events_total: number
  tenants: VolumeTenant[]
  /** Só vem preenchido com `tenantId` (e sempre na rota de cliente). */
  endpoints: VolumeEndpoint[] | null
}

// ── histórico paginado (§5) ──────────────────────────────────────────────────

export interface LogHistoryQuery {
  endpointId?: string
  tenantId?: string
  eventType?: string
  source?: string
  nativeId?: string
  /**
   * Timeline/árvore de um processo pelo ProcessGuid. NÃO exige escopo — tem
   * índice próprio e dá `event_time_desc` no `order` sozinho (§HERMES §5, §6).
   */
  processEntityId?: string
  /** Verbo fino (`process_access`, …). Exige `tenantId` ou `endpointId` (§5). */
  eventAction?: string
  /** Usuário ator numa máquina/tenant. Exige `tenantId` ou `endpointId` (§5). */
  userName?: string
  /**
   * Módulo de origem — lista fechada (`etw`|`wfp`|`ebpf`|`journald`|`proc`|…). O
   * texto de fio vai literal. Exige `tenantId` ou `endpointId`; fora do
   * vocabulário devolve `logs_event_module_invalid` (§5).
   */
  eventModule?: string
  /** Nome curto do processo (`process_name`). Exige `tenantId` ou `endpointId` (§5). */
  processName?: string
  /** Nome do serviço/unidade. Exige `tenantId` ou `endpointId` (§5). */
  serviceName?: string
  /** Extensão do arquivo, com ou sem ponto — o servidor normaliza. Exige escopo (§5). */
  fileExtension?: string
  /**
   * Consulta DNS — igualdade EXATA, não substring. Pivô de frota: NÃO exige escopo
   * e sustenta `from`/`to` sozinho (§12.4).
   */
  dnsQuestion?: string
  /** Fan-out: eventos que tocaram um IP. Sem escopo (índice GIN) (§5, §9). */
  relatedIp?: string
  /** Fan-out: eventos de um usuário na frota. Sem escopo (índice GIN) (§5, §9). */
  relatedUser?: string
  /** Caça a IOC: todo host que viu um binário. Sem escopo (índice GIN) (§5, §11). */
  relatedHash?: string
  from?: string
  to?: string
  /** Opaco e versionado: devolva sem alterar, sob pena de `logs_cursor_invalid`. */
  cursor?: string | null
  pageSize?: number
}

export interface LogPage {
  items: LogRow[]
  /** O EFETIVO: o servidor grampeia em 200 silenciosamente. */
  page_size: number
  /** `false` é o fim. Não se infere do tamanho da página. */
  has_more: boolean
  next_cursor: string | null
  /** MUDA com o filtro: 'id_desc' sem escopo, 'event_time_desc' com escopo (§5.3). */
  order: string
}

// ── centro de controle (§6) ──────────────────────────────────────────────────

export type CommandParams =
  | { artifact: 'system_snapshot' }
  | { artifact: 'memory_dump' }
  | { artifact: 'event_log' }
  | { artifact: 'file'; path: string }

export interface CommandDispatch {
  /** "Unavailable" enquanto não há despachante. */
  state: string
  unavailable_reason: string | null
  /** Vem PRONTO do servidor, em português. Exiba literalmente (§6.4). */
  warning: string | null
}

export interface CommandDefinition {
  kind: string
  /** Prontos, em português. Use-os (§6.1). */
  label: string
  description: string
  severity: string
  requires_confirmation: boolean
  requires_admin: boolean
  requires_reason: boolean
  ttl_seconds: number | null
  /** O comando que desfaz este: `isolate_host` → `restore_host` (§6.9). */
  reversed_by: string | null
  available: boolean
  unavailable_reason: string | null
}

export interface CommandCatalog {
  endpoint_id: string | null
  dispatch: CommandDispatch
  commands: CommandDefinition[]
}

/**
 * Estado do comando para o OPERADOR. Use este, não `status` (estado interno da
 * fila). Só `executed` e `skipped` significam que o agente confirmou; `delivered`
 * é "escrito na stream", SEM confirmação. Aberto: valor novo vai como veio.
 */
export type CommandState =
  | 'pending'
  | 'delivered'
  | 'executed'
  | 'skipped'
  | 'failed'
  | 'expired'
  | 'cancelled'
  | (string & {})

/**
 * Isolar/desbloquear host, regime de isolamento, os comandos do firewall de IP
 * do host e os do Web Defense. Vocabulário aberto: outros kinds vão como vieram.
 */
export type AgentCommandKind =
  | 'isolate_host'
  | 'restore_host'
  | 'set_isolation_policy'
  | 'block_ip'
  | 'unblock_ip'
  | 'allow_ip'
  | 'revoke_allow_ip'
  | 'resolve_challenge'
  | 'challenge_ip'
  | 'list_web_exceptions'
  | 'revoke_web_exception'
  | 'uninstall_agent'
  | (string & {})

/** O que o agente reportou. `null` no comando enquanto ele não reportou. */
export interface CommandResult {
  status: string
  detail: string | null
  finished_at: string
}

/**
 * Um comando na fila. O MESMO objeto (snake_case) no console (`/api/admin`) e
 * na API de gestão (`/api/v1/commands/{id}`, `/machines/{id}/commands`).
 */
export interface AgentCommand {
  id: string
  endpoint_id: string
  tenant_id: string
  /** `restore_host`, `block_ip`, `unblock_ip`, `allow_ip`, `revoke_allow_ip`, … Aberto. */
  kind: AgentCommandKind
  /**
   * Comandos de IP: JSON em string com o alvo CANÔNICO — `'{"ip":"…"}'` ou
   * `'{"cidr":"…"}'`. Leia por `commandTarget` (lib/ip/semantics.ts).
   */
  params?: string | null
  /** Estado interno da fila, com a expiração aplicada. A tela lê `state`. */
  status: string
  state: CommandState
  /** Só existe quando o agente reportou. */
  result: CommandResult | null
  severity: string | null
  reason: string | null
  requested_by: string | null
  requested_at: string
  expires_at: string | null
  /** Quando foi escrito na stream do agente pela primeira vez. NÃO é confirmação. */
  dispatched_at: string | null
  completed_at: string | null
  failure_code: string | null
  failure_detail: string | null
  attempt_count: number
  /** Resolvido no servidor considerando expiração e estado. Não infira do status. */
  is_cancellable: boolean
  dispatch: CommandDispatch
}

export interface EnqueueCommandBody {
  kind: string
  /** Só `collect_artifact` aceita; qualquer outro tipo exige vazio/nulo (§6.6). */
  params?: CommandParams | string | null
  reason?: string | null
  /** Gere uma por CLIQUE: `crypto.randomUUID()` (§6.5). */
  idempotencyKey: string
  /** Escolha do OPERADOR, não constante do cliente (§6.4). */
  acknowledgeNotDeliverable: boolean
}

/**
 * Envelope paginado do /api/admin (snake_case).
 *
 * As listagens de comando (§6.7) guardam ações humanas: milhares de linhas, não
 * bilhões, então o `count(*)` é barato e há total e página — ao contrário do
 * histórico de logs (§5.2). `adminPageProps` mapeia isto para o `Pagination` do
 * projeto.
 */
export interface AdminPage<T> {
  items: T[]
  page: number
  page_size: number
  total_count: number
  total_pages: number
}

export function adminPageProps<T>(page: AdminPage<T>): {
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
} {
  return {
    page: page.page,
    pageSize: page.page_size,
    totalCount: page.total_count,
    totalPages: page.total_pages,
  }
}

export interface CommandListQuery {
  page?: number
  pageSize?: number
  /**
   * Só na frota (`GET /api/admin/commands`): tipo em snake_case e só os não
   * terminais. Não está na referência da API; confirmado em
   * `AgentCommandListQuery` (Athena.Contracts).
   */
  kind?: string
  openOnly?: boolean
}

// ── processo: árvore e linha do tempo (§12.2, §12.3) ────────────────────────

export interface ProcessTimelineQuery {
  from?: string
  to?: string
  eventType?: string
  /** Opaco: devolva `next_cursor` sem alterar. */
  cursor?: string | null
  pageSize?: number
}

// ── recursos ─────────────────────────────────────────────────────────────────

/**
 * Passa o corpo pelo decodificador defensivo. Uma forma que ele recusa vira
 * erro de tela — nunca um objeto meio-tipado dentro da árvore React.
 */
// ── detecções ────────────────────────────────────────────────────────────────

export interface DetectionQuery {
  tenantId?: string
  endpointId?: string
  processEntityId?: string
  /** Fora de low|medium|high|critical o servidor devolve 400. */
  severity?: DetectionSeverity
  ruleId?: string
  /**
   * Ação de resposta do agente (`kill_process`, `isolate_host`, …). Com
   * `responseStatus`, os dois casam na MESMA resposta do alerta. Fora de
   * `[a-z0-9_]{1,64}`: `400 detections_response_action_invalid`.
   */
  responseAction?: string
  /**
   * Desfecho (`executed`, `simulated`, `skipped`, `failed`, `recommended`, …).
   * Mesma regra. `isolate_host` + `recommended` = alertas à espera de decisão.
   */
  responseStatus?: string
  /** Só alertas de máquinas isoladas AGORA. `false` é o mesmo que omitir. */
  isolatedMachines?: boolean
  from?: string
  to?: string
  cursor?: string | null
  pageSize?: number
}

export function decoded<T>(decode: (v: unknown) => T | null) {
  return (body: unknown): T => {
    const value = decode(body)
    if (value === null) {
      throw new AthenaError({
        status: 502,
        title: 'Resposta fora do contrato',
        detail: 'O servidor respondeu num formato que o console não reconhece.',
        code: 'response_shape_invalid',
      })
    }
    return value
  }
}

/** Constrói a rota de detalhe a partir do identificador que a linha carrega (§3.1). */
export function detailPath(row: LogRow): string {
  if (row.id !== null) return `/logs/${row.id}`
  // Passe os campos COMO VIERAM: o servidor trata eventTime como janela de poucos
  // ms, não como igualdade, por causa da diferença de precisão PG × .NET (§3.1).
  return `/logs/resolve${query({
    endpointId: row.endpoint_id,
    eventTime: row.event_time,
    recordId: row.record_id,
    nativeId: row.native_id,
  })}`
}

export const admin = {
  tenants: (signal?: AbortSignal) =>
    call<TenantDirectory>('/tenants', { base: ADMIN_BASE, signal }),

  agents: (params: AgentDirectoryQuery, signal?: AbortSignal) =>
    call<AgentDirectoryPage>(
      `/agents${query({
        page: params.page,
        pageSize: params.pageSize,
        tenantId: params.tenantId,
        search: params.search,
      })}`,
      { base: ADMIN_BASE, signal },
    ),

  logs: (params: LogHistoryQuery, signal?: AbortSignal) =>
    call<LogPage>(
      `/logs${query({
        endpointId: params.endpointId,
        tenantId: params.tenantId,
        eventType: params.eventType,
        source: params.source,
        nativeId: params.nativeId,
        processEntityId: params.processEntityId,
        eventAction: params.eventAction,
        userName: params.userName,
        eventModule: params.eventModule,
        processName: params.processName,
        serviceName: params.serviceName,
        fileExtension: params.fileExtension,
        dnsQuestion: params.dnsQuestion,
        relatedIp: params.relatedIp,
        relatedUser: params.relatedUser,
        relatedHash: params.relatedHash,
        from: params.from,
        to: params.to,
        cursor: params.cursor,
        pageSize: params.pageSize,
      })}`,
      { base: ADMIN_BASE, signal },
    ),

  logDetail: (row: LogRow, signal?: AbortSignal): Promise<LogDetail> =>
    call<unknown>(detailPath(row), { base: ADMIN_BASE, signal }).then(decoded(decodeLogDetail)),

  /**
   * Uma camada da árvore: o nó, os ancestrais e os filhos diretos. Para descer,
   * peça a árvore do filho. `404 process_not_found` quando o id não tem evento.
   */
  processTree: (entityId: string, signal?: AbortSignal): Promise<ProcessTree> =>
    call<unknown>(`/processes/${encodeURIComponent(entityId)}`, {
      base: ADMIN_BASE,
      signal,
    }).then(decoded(decodeProcessTree)),

  /** Ordem crescente (`event_time_asc`), paginada por `next_cursor`. */
  processTimeline: (
    entityId: string,
    params: ProcessTimelineQuery,
    signal?: AbortSignal,
  ): Promise<ProcessTimelinePage> =>
    call<unknown>(
      `/processes/${encodeURIComponent(entityId)}/timeline${query({
        from: params.from,
        to: params.to,
        eventType: params.eventType,
        cursor: params.cursor,
        pageSize: params.pageSize,
      })}`,
      { base: ADMIN_BASE, signal },
    ).then(decoded(decodeTimelinePage)),

  /**
   * Fila de alertas de detecção, do mais novo para o mais antigo (ordem de
   * chegada). Sem escopo é permitido; sem total, como o histórico.
   */
  detections: (params: DetectionQuery, signal?: AbortSignal): Promise<DetectionPage> =>
    call<unknown>(
      `/detections${query({
        tenantId: params.tenantId,
        endpointId: params.endpointId,
        processEntityId: params.processEntityId,
        severity: params.severity,
        ruleId: params.ruleId,
        responseAction: params.responseAction,
        responseStatus: params.responseStatus,
        isolatedMachines: params.isolatedMachines === true ? true : undefined,
        from: params.from,
        to: params.to,
        cursor: params.cursor,
        pageSize: params.pageSize,
      })}`,
      { base: ADMIN_BASE, signal },
    ).then(decoded(decodeDetectionPage)),

  /** Detecções por empresa e severidade. Consulta ao banco: poll de 30–60 s, nunca a cada tique. */
  detectionStats: (params: DetectionStatsQuery, signal?: AbortSignal) =>
    call<DetectionStats>(
      `/stats/detections${query({ from: params.from, to: params.to, tenantId: params.tenantId })}`,
      { base: ADMIN_BASE, signal },
    ),

  /** Volume ACUMULADO por empresa; com `tenantId`, também por agente. */
  volumeStats: (params: { tenantId?: string } = {}, signal?: AbortSignal) =>
    call<VolumeStats>(`/stats/volume${query({ tenantId: params.tenantId })}`, {
      base: ADMIN_BASE,
      signal,
    }),

  commandCatalog: (endpointId?: string, signal?: AbortSignal) =>
    call<CommandCatalog>(`/commands/catalog${query({ endpointId })}`, {
      base: ADMIN_BASE,
      signal,
    }),

  enqueueCommand: (endpointId: string, body: EnqueueCommandBody) =>
    call<AgentCommand>(`/agents/${endpointId}/commands`, {
      base: ADMIN_BASE,
      method: 'POST',
      body,
    }),

  /** Histórico de comandos de uma máquina. */
  commandsForEndpoint: (endpointId: string, params: CommandListQuery, signal?: AbortSignal) =>
    call<AdminPage<AgentCommand>>(
      `/agents/${endpointId}/commands${query({ page: params.page, pageSize: params.pageSize })}`,
      { base: ADMIN_BASE, signal },
    ),

  /** Frota inteira — revisão de incidente. */
  commands: (params: CommandListQuery, signal?: AbortSignal) =>
    call<AdminPage<AgentCommand>>(
      `/commands${query({
        page: params.page,
        pageSize: params.pageSize,
        kind: params.kind,
        openOnly: params.openOnly,
      })}`,
      { base: ADMIN_BASE, signal },
    ),

  command: (id: string, signal?: AbortSignal) =>
    call<AgentCommand>(`/commands/${id}`, { base: ADMIN_BASE, signal }),

  cancelCommand: (id: string) =>
    call<AgentCommand>(`/commands/${id}/cancel`, { base: ADMIN_BASE, method: 'POST' }),
}
